import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import TimelineIcon from "@material-ui/icons/Timeline";
import LocalTaxiIcon from "@material-ui/icons/LocalTaxi";
import MotorcycleIcon from "@material-ui/icons/Motorcycle";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import StopIcon from "@material-ui/icons/Stop";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import TripDetails from "./TripDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { Stars, readableError, humanize } from "../detailFields";
import "../superRide.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const TRIP_FILTERS = `
  _id: $_id
  tripType: $tripType
  ended: $ended
  state: $state
  costFrom: $costFrom
  cost: $cost
  driverName: $driverName
  driverPhoneNumber: $driverPhoneNumber
  passengerName: $passengerName
  passengerPhoneNumber: $passengerPhoneNumber
  startDateFrom: $startDateFrom
  startDate: $startDate
  endDateFrom: $endDateFrom
  endDate: $endDate
`;

const TRIP_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $tripType: TripType
  $ended: Boolean
  $state: StateInput
  $costFrom: Float
  $cost: Float
  $driverName: String
  $driverPhoneNumber: String
  $passengerName: String
  $passengerPhoneNumber: String
  $startDateFrom: Date
  $startDate: Date
  $endDateFrom: Date
  $endDate: Date
  $costSort: Int
  $startDateSort: Int
  $endDateSort: Int
`;

const GET_TRIPS = gql`
  query getTripsByAdmin(${TRIP_VARIABLES}) {
    getTripsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${TRIP_FILTERS} }
      sort: { cost: $costSort, startDate: $startDateSort, endDate: $endDateSort }
    ) {
      _id
      tripType
      state
      ended
      cost
      rate
      tripDistance
      createdAt
      startDate
      endDate
      staticMapImageUrl
      waitTimePrice
      optionsPrice
      optionsPriceDetails {
        option
        price
      }
      tipValue
      driver {
        _id
        fullName
        phoneNumber
      }
      passengerDetail {
        _id
        fullName
        phoneNumber
      }
      car {
        _id
      }
    }
    getTripsByAdminCount(filters: { ${TRIP_FILTERS} })
  }
`;

// Platform-wide totals rather than a sum of whatever page is on screen.
const GET_TRIP_COUNTS = gql`
  query getTripSummaryCounts {
    inProgress: getTripsByAdminCount(filters: { ended: false })
    completed: getTripsByAdminCount(filters: { ended: true })
    ride: getTripsByAdminCount(filters: { tripType: RIDE })
    delivery: getTripsByAdminCount(filters: { tripType: DELIVERY })
  }
`;

const END_TRIP = gql`
  mutation endTripByAdmin($tripId: ID!) {
    endTripByAdmin(tripId: $tripId) {
      _id
      ended
      state
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

// Taken from the Trip model's own enum. The filter dropdown used to
// offer COMING, which the model can never produce, and omitted PENDING,
// which it can — so one option always returned nothing and one real
// state could not be filtered for at all.
const STATE_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "RESERVED", label: "Reserved" },
  { value: "SEARCHING", label: "Searching" },
  { value: "ACCEPTED", label: "Accepted" },
  { value: "ARRIVED", label: "Arrived" },
  { value: "WAITING", label: "Waiting" },
  { value: "PICKED_UP", label: "Picked up" },
  { value: "DESTINATION", label: "At destination" },
  { value: "DRIVER_CANCELED", label: "Driver canceled" },
  { value: "PASSENGER_CANCELED", label: "Passenger canceled" },
  { value: "PASSENGER_CANCELED_DURING_TRIP", label: "Canceled during trip" },
  { value: "FINISHED_DUE_TO_NOT_PAYING", label: "Ended, not paid" },
];
const STATE_LABELS = STATE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATE_TONES = {
  PENDING: "neutral",
  RESERVED: "info",
  SEARCHING: "info",
  ACCEPTED: "info",
  ARRIVED: "warning",
  WAITING: "warning",
  PICKED_UP: "success",
  DESTINATION: "success",
  DRIVER_CANCELED: "danger",
  PASSENGER_CANCELED: "danger",
  PASSENGER_CANCELED_DURING_TRIP: "danger",
  FINISHED_DUE_TO_NOT_PAYING: "danger",
};

const TYPE_OPTIONS = [
  { value: "RIDE", label: "Ride" },
  { value: "DELIVERY", label: "Delivery" },
];
const TYPE_LABELS = { RIDE: "Ride", DELIVERY: "Delivery" };

const PROGRESS_OPTIONS = [
  { value: "", label: "Any progress" },
  { value: "false", label: "In progress" },
  { value: "true", label: "Completed" },
];
const PROGRESS_LABELS = PROGRESS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_TARGETS = [
  { value: "driver", label: "Driver" },
  { value: "passenger", label: "Passenger" },
  { value: "_id", label: "Trip ID" },
];
const SEARCH_LABELS = SEARCH_TARGETS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const SEARCH_PLACEHOLDERS = {
  driver: "Search by driver name or phone number...",
  passenger: "Search by passenger name or phone number...",
  _id: "Search by trip ID (24 characters)...",
};

const SORT_OPTIONS = [
  { value: "startNewest", label: "Newest first" },
  { value: "startOldest", label: "Oldest first" },
  { value: "costHigh", label: "Cost high-low" },
  { value: "costLow", label: "Cost low-high" },
  { value: "endRecent", label: "Recently ended" },
];

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const isObjectId = (value) => /^[0-9a-fA-F]{24}$/.test(value);
const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

// A trip that has not ended and is not in a cancelled state is one an
// admin can still force-end.
const CANCELLED_STATES = [
  "DRIVER_CANCELED",
  "PASSENGER_CANCELED",
  "PASSENGER_CANCELED_DURING_TRIP",
  "FINISHED_DUE_TO_NOT_PAYING",
];
const canEnd = (trip) => !trip.ended && CANCELLED_STATES.indexOf(trip.state) === -1;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

/**
 * Trips - every trip on the platform.
 *
 * Also serves /superAdmin/tripHistory/:driverName/:driverPhoneNumber,
 * where the layout passes a driver in and the screen opens pre-filtered
 * to them.
 */
const Trips = (props) => {
  const pinnedDriver = props.driverName || props.driverPhoneNumber;

  const [searchTarget, setSearchTarget] = useState("driver");
  const [searchTerm, setSearchTerm] = useState(props.driverName || props.driverPhoneNumber || "");
  const [typeFilter, setTypeFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [progressFilter, setProgressFilter] = useState("");
  const [costFrom, setCostFrom] = useState("");
  const [costTo, setCostTo] = useState("");
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [endFrom, setEndFrom] = useState("");
  const [endTo, setEndTo] = useState("");
  const [sortOption, setSortOption] = useState("startNewest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [tripDetail, setTripDetail] = useState(null);
  const [endTarget, setEndTarget] = useState(null);
  const [ending, setEnding] = useState(false);
  const [actionError, setActionError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    startDateSort: -1,
    driverName: props.driverName || undefined,
    driverPhoneNumber: props.driverPhoneNumber || undefined,
  });

  const trimmedTerm = searchTerm.trim();
  const searchIsUsable = !trimmedTerm || searchTarget !== "_id" || isObjectId(trimmedTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      const search = {};
      if (term) {
        if (searchTarget === "_id") {
          if (isObjectId(term)) search._id = term;
        } else if (looksLikePhone(term)) {
          search[`${searchTarget}PhoneNumber`] = term;
        } else {
          search[`${searchTarget}Name`] = term;
        }
      }

      setVariables((v) => ({
        ...v,
        skip: 0,
        _id: undefined,
        driverName: undefined,
        driverPhoneNumber: undefined,
        passengerName: undefined,
        passengerPhoneNumber: undefined,
        ...search,
        tripType: typeFilter || undefined,
        state: stateFilter || undefined,
        ended: progressFilter === "" ? undefined : progressFilter === "true",
        costFrom: costFrom ? parseFloat(costFrom) : undefined,
        cost: costTo ? parseFloat(costTo) : undefined,
        startDateFrom: startFrom || undefined,
        startDate: startTo || undefined,
        endDateFrom: endFrom || undefined,
        endDate: endTo || undefined,
        costSort: sortOption === "costHigh" ? -1 : sortOption === "costLow" ? 1 : undefined,
        startDateSort:
          sortOption === "startNewest" ? -1 : sortOption === "startOldest" ? 1 : undefined,
        endDateSort: sortOption === "endRecent" ? -1 : undefined,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [
    searchTarget,
    searchTerm,
    typeFilter,
    stateFilter,
    progressFilter,
    costFrom,
    costTo,
    startFrom,
    startTo,
    endFrom,
    endTo,
    sortOption,
  ]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_TRIPS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: countsData, refetch: refetchCounts } = useQuery(GET_TRIP_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_TRIPS, {
    fetchPolicy: "network-only",
  });

  const [endTripByAdmin] = useMutation(END_TRIP);

  const trips = (data && data.getTripsByAdmin) || [];
  const totalCount = (data && data.getTripsByAdminCount) || 0;
  const counts = countsData || {};
  const pageCost = trips.reduce((sum, t) => sum + (t.cost || 0), 0);

  /* ---------------- end trip ---------------- */

  const runEndTrip = () => {
    if (!endTarget) return;
    setEnding(true);
    setActionError("");
    endTripByAdmin({ variables: { tripId: endTarget._id } })
      .then(() => {
        setEnding(false);
        setEndTarget(null);
        setTripDetail(null);
        refetch();
        refetchCounts();
      })
      .catch((err) => {
        setEnding(false);
        setEndTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (stateFilter) activeFilterChips.push({ id: "state", label: `State: ${STATE_LABELS[stateFilter]}` });
  if (progressFilter) {
    activeFilterChips.push({ id: "progress", label: PROGRESS_LABELS[progressFilter] });
  }
  if (costFrom || costTo) {
    activeFilterChips.push({ id: "cost", label: `Cost: $${costFrom || "0"} to $${costTo || "-"}` });
  }
  if (startFrom || startTo) {
    activeFilterChips.push({ id: "start", label: `Started: ${startFrom || "-"} to ${startTo || "-"}` });
  }
  if (endFrom || endTo) {
    activeFilterChips.push({ id: "end", label: `Ended: ${endFrom || "-"} to ${endTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "type") setTypeFilter("");
    if (id === "state") setStateFilter("");
    if (id === "progress") setProgressFilter("");
    if (id === "cost") {
      setCostFrom("");
      setCostTo("");
    }
    if (id === "start") {
      setStartFrom("");
      setStartTo("");
    }
    if (id === "end") {
      setEndFrom("");
      setEndTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setTypeFilter("");
    setStateFilter("");
    setProgressFilter("");
    setCostFrom("");
    setCostTo("");
    setStartFrom("");
    setStartTo("");
    setEndFrom("");
    setEndTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      [
        "Trip ID",
        "Type",
        "State",
        "Driver",
        "Driver phone",
        "Passenger",
        "Passenger phone",
        "Cost",
        "Distance (km)",
        "Rating",
        "Started",
        "Ended",
      ],
    ];
    exportData.getTripsByAdmin.forEach((t) => {
      rows.push([
        t._id,
        TYPE_LABELS[t.tripType] || t.tripType || "-",
        STATE_LABELS[t.state] || t.state || "-",
        (t.driver && t.driver.fullName) || "-",
        (t.driver && t.driver.phoneNumber) || "-",
        (t.passengerDetail && t.passengerDetail.fullName) || "-",
        (t.passengerDetail && t.passengerDetail.phoneNumber) || "-",
        Number(t.cost || 0).toFixed(2),
        t.tripDistance || 0,
        t.rate || "-",
        t.startDate ? moment(t.startDate).format("MM/DD/YYYY HH:mm") : "-",
        t.endDate ? moment(t.endDate).format("MM/DD/YYYY HH:mm") : "-",
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Trips");
    XLSX.writeFile(wb, "Trips.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  /* ---------------- render ---------------- */

  const title = pinnedDriver ? "Trip history" : "Trips";
  const description = pinnedDriver
    ? `Every trip recorded for ${props.driverName || props.driverPhoneNumber}.`
    : "Every ride and delivery on the platform, and how each one ended.";

  if (loading && !data) {
    return (
      <div className="page superRidePage superTripsPage">
        <main className="main">
          <PageHeader title={title} description={description} />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page superRidePage superTripsPage">
      <main className="main">
        <PageHeader
          title={title}
          count={totalCount}
          description={description}
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} trip${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard
            icon={<TimelineIcon />}
            title="Matching trips"
            value={totalCount}
            hint={activeFilterChips.length > 0 ? "With current filters" : "All trips"}
          />
          <MetricCard
            icon={<PlayCircleOutlineIcon />}
            title="In progress"
            value={counts.inProgress || 0}
            tone={counts.inProgress > 0 ? "info" : "default"}
          />
          <MetricCard icon={<LocalTaxiIcon />} title="Rides" value={counts.ride || 0} />
          <MetricCard icon={<MotorcycleIcon />} title="Deliveries" value={counts.delivery || 0} />
        </section>

        <section className="filters">
          <SearchField
            placeholder={SEARCH_PLACEHOLDERS[searchTarget]}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "Rides and deliveries" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <FilterDropdown
              label={stateFilter ? `State: ${STATE_LABELS[stateFilter]}` : "State"}
              options={[{ value: "", label: "All states" }, ...STATE_OPTIONS]}
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            />
            <FilterDropdown
              label={progressFilter ? PROGRESS_LABELS[progressFilter] : "Progress"}
              options={PROGRESS_OPTIONS}
              value={progressFilter}
              onChange={(e) => setProgressFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Cost"
              fromValue={costFrom}
              toValue={costTo}
              onFromChange={(e) => setCostFrom(e.target.value)}
              onToChange={(e) => setCostTo(e.target.value)}
            />
            <DateRangeFilter
              label="Started"
              fromValue={startFrom}
              toValue={startTo}
              onFromChange={(e) => setStartFrom(e.target.value)}
              onToChange={(e) => setStartTo(e.target.value)}
            />
            <DateRangeFilter
              label="Ended"
              fromValue={endFrom}
              toValue={endTo}
              onFromChange={(e) => setEndFrom(e.target.value)}
              onToChange={(e) => setEndTo(e.target.value)}
              align="right"
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {!searchIsUsable && (
          <div className="dsFormMessage warning" role="status">
            A trip ID is 24 characters — still waiting for a complete one before searching.
          </div>
        )}

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {trips.length > 0 && (
              <span className="superRidePill">This page: {money(pageCost)}</span>
            )}
          </div>
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh trips"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superRideSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Trip</div>
              <div className="tableHeaderCell">Driver</div>
              <div className="tableHeaderCell">Passenger</div>
              <div className="tableHeaderCell">State</div>
              <div className="tableHeaderCell">Cost</div>
              <div className="tableHeaderCell">Started</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {trips.length === 0 ? (
              <EmptyState
                icon={<TimelineIcon />}
                title={activeFilterChips.length === 0 ? "No trips yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Trips appear here as customers start booking."
                    : "No trips match these filters. Try widening or clearing them."
                }
              />
            ) : (
              trips.map((trip) => (
                <div className="row" key={trip._id}>
                  <div className="superRideStack" data-label="Trip">
                    <span className="superRideTag">
                      {trip.tripType === "DELIVERY" ? (
                        <MotorcycleIcon style={{ fontSize: 14, marginRight: 4 }} />
                      ) : (
                        <LocalTaxiIcon style={{ fontSize: 14, marginRight: 4 }} />
                      )}
                      {TYPE_LABELS[trip.tripType] || humanize(trip.tripType)}
                    </span>
                    <Stars value={trip.rate} showValue={false} />
                  </div>

                  <div className="superRidePerson" data-label="Driver">
                    {trip.driver ? (
                      <React.Fragment>
                        <span className="superRidePersonName">{trip.driver.fullName}</span>
                        <span className="superRidePersonMeta">{trip.driver.phoneNumber}</span>
                      </React.Fragment>
                    ) : (
                      <span className="superRideMuted">Unassigned</span>
                    )}
                  </div>

                  <div className="superRidePerson" data-label="Passenger">
                    {trip.passengerDetail ? (
                      <React.Fragment>
                        <span className="superRidePersonName">{trip.passengerDetail.fullName}</span>
                        <span className="superRidePersonMeta">{trip.passengerDetail.phoneNumber}</span>
                      </React.Fragment>
                    ) : (
                      <span className="superRideMuted">Unknown</span>
                    )}
                  </div>

                  <span data-label="State">
                    <StatusBadge
                      status={STATE_TONES[trip.state] || "neutral"}
                      label={STATE_LABELS[trip.state] || humanize(trip.state)}
                    />
                  </span>

                  <span className="superRideAmount" data-label="Cost">
                    {money(trip.cost)}
                  </span>

                  <span className="updated" data-label="Started">
                    {trip.startDate ? (
                      <React.Fragment>
                        <strong>{formatDate(trip.startDate)}</strong>
                        <span>{formatTime(trip.startDate)}</span>
                      </React.Fragment>
                    ) : (
                      <span className="superRideMuted">Not started</span>
                    )}
                  </span>

                  <div className="actions">
                    {canEnd(trip) && (
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setEndTarget(trip)}
                        title="Force end this trip"
                      >
                        <StopIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => setTripDetail(trip)}
                      title="Trip details"
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(tripDetail)}
        onClose={() => setTripDetail(null)}
        title="Trip details"
        subtitle={tripDetail ? `#${tripDetail._id.slice(-8).toUpperCase()}` : ""}
        size="lg"
      >
        {tripDetail && (
          <TripDetails
            trip={tripDetail}
            close={() => setTripDetail(null)}
            refetch={() => {
              refetch();
              refetchCounts();
            }}
            onRequestEnd={() => setEndTarget(tripDetail)}
          />
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(endTarget)}
        onClose={() => (ending ? null : setEndTarget(null))}
        title="End this trip"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setEndTarget(null), disabled: ending },
          { label: "End trip", onClick: runEndTrip, variant: "danger", loading: ending },
        ]}
      >
        <div className="superRideConfirm">
          The trip will be closed out and charged as it stands. This cannot be undone.
        </div>
      </DialogShell>
    </div>
  );
};

export default Trips;
